import styled from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'

import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoError
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoErrorRootProps = ComponentPropsWithoutRef<'div'>

const MedicationPhotoErrorRoot = ({ children, ...props }: MedicationPhotoErrorRootProps) => (
  <StRoot {...props}>{children}</StRoot>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoError.Illustration
 * ------------------------------------------------------------------------------------------------- */

/** `alt` is required: describe the photo, or pass `''` only when the image is purely decorative. */
type MedicationPhotoErrorIllustrationProps = ComponentPropsWithoutRef<'img'> & { alt: string }

const MedicationPhotoErrorIllustration = (props: MedicationPhotoErrorIllustrationProps) => <StIllustration {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoError.Title
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoErrorTitleProps = ComponentPropsWithoutRef<typeof H2>

const MedicationPhotoErrorTitle = ({ children, ...props }: MedicationPhotoErrorTitleProps) => (
  <StTitle noMargin {...props}>
    {children}
  </StTitle>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoError.Description
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoErrorDescriptionProps = ComponentPropsWithoutRef<typeof Span>

const MedicationPhotoErrorDescription = ({ children, ...props }: MedicationPhotoErrorDescriptionProps) => (
  <StDescription type='BodyMediumRegular' {...props}>
    {children}
  </StDescription>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoError.Actions
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoErrorActionsProps = ComponentPropsWithoutRef<'div'>

const MedicationPhotoErrorActions = ({ children, ...props }: MedicationPhotoErrorActionsProps) => (
  <StActions {...props}>{children}</StActions>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const MedicationPhotoError = Object.assign(MedicationPhotoErrorRoot, {
  Illustration: MedicationPhotoErrorIllustration,
  Title: MedicationPhotoErrorTitle,
  Description: MedicationPhotoErrorDescription,
  Actions: MedicationPhotoErrorActions,
})

export { MedicationPhotoError }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StRoot = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: 100%;

  ${({ theme }) => `
    gap: ${theme.UI.SpacingPx.Space4};
  `}
`

const StIllustration = styled.img`
  max-width: 100%;
  height: auto;

  ${({ theme }) => `
    margin-bottom: ${theme.UI.SpacingPx.Space6};
  `}
`

const StTitle = styled(H2)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  text-align: center;
`

const StDescription = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`

const StActions = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  margin-top: auto;

  ${({ theme }) => `
    gap: ${theme.UI.SpacingPx.Space3};
  `}
`
